"use client"

import dynamic from "next/dynamic"
import { useSyncExternalStore } from "react"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"

const NewsletterSignupModal = dynamic(
  () => import("@/components/ui/newsletter-signup-modal").then((m) => m.NewsletterSignupModal),
  { ssr: false, loading: () => null }
)

const ExitScanModal = dynamic(
  () => import("@/components/blog/exit-scan-modal").then((m) => m.ExitScanModal),
  { ssr: false, loading: () => null }
)

interface ContentNewsletterModalProps {
  /**
   * Surface name threaded into the modal's analytics source (`<source>_modal`)
   * and subscribe context — e.g. "blog", "guide", "costs".
   */
  source: string
  /** Scroll-depth trigger; content pages default lower than tools' 0.85. */
  scrollThreshold?: number
  /**
   * Post category (blog) or grass/topic name (guide) so the modal pitches
   * the reader's topic-specific care calendar instead of a generic
   * newsletter. Optional — omitting falls back to the generic calendar copy.
   */
  category?: string
  /**
   * Post slug (blog only). Picks the task-specific offer (lib/newsletter-offer)
   * and the scan modal's grass-named headings.
   */
  slug?: string
  /**
   * Server-computed isIdentifyAudience(slug, category). When true on a blog
   * post, the exit-intent scan modal replaces the newsletter body on the SAME
   * trigger. Trigger, session cap, and
   * dismissal cooldown stay shared either way.
   */
  identifyIntent?: boolean
  /**
   * Active subscriber count, rounded down (lib/newsletter-subscriber-count).
   * Shown as social proof on the yes/no step; null hides the line.
   */
  subscriberCount?: number | null
}

const COARSE_POINTER_QUERY = "(pointer: coarse)"
const hasMatchMedia = () => typeof window !== "undefined" && typeof window.matchMedia === "function"
const subscribeCoarsePointer = (onChange: () => void) => {
  if (!hasMatchMedia()) return () => {}
  const mql = window.matchMedia(COARSE_POINTER_QUERY)
  mql.addEventListener("change", onChange)
  return () => mql.removeEventListener("change", onChange)
}
const getCoarsePointer = () => hasMatchMedia() && window.matchMedia(COARSE_POINTER_QUERY).matches
const getCoarsePointerServer = () => false

/**
 * Exit-intent / scroll-depth newsletter modal for content pages (blog posts,
 * guides, /costs). Before this, the modal only existed on tool pages, so ~81%
 * of traffic (blog) could bounce without ever seeing an email ask — the July
 * 2026 PostHog audit measured only 12.7% of blog visitors ever saw ANY
 * capture surface. This is a passive fallback: it never fires for
 * authenticated users, dismissal starts a cooldown (see usage-tracker), and any
 * successful capture (including the inline widgets) suppresses it permanently.
 */
export function ContentNewsletterModal({
  source,
  scrollThreshold = 0.5,
  category,
  slug,
  identifyIntent,
  subscriberCount = null,
}: ContentNewsletterModalProps) {
  // Touch devices have no mouse-leave signal, so the 45s timer was the signal
  // most likely to fire mid-read there (mobile closed the modal 79% of the
  // time vs 68% on desktop, Aug-Sep 2026). Phones keep the scroll-based
  // signals only; desktop keeps the timer.
  const coarsePointer = useSyncExternalStore(
    subscribeCoarsePointer,
    getCoarsePointer,
    getCoarsePointerServer
  )

  const { showNewsletterModal, closeNewsletterModal, triggerSignal } = useNewsletterPrompt({
    passiveTriggers: true,
    scrollThreshold,
    // On articles, scroll depth is an engagement signal, not an exit signal.
    // Reaching 50% only ARMS the trigger; it fires on scroll-up (leave
    // gesture) or read-complete (95%). Day-one blog data without this:
    // ~70% explicit dismissals from firing mid-read.
    requireScrollUpAfterDepth: true,
    // Second fire signal: an armed reader who stops scrolling for 4s has
    // paused, not left. Depth plus a pause is the two-signal read the popup
    // benchmarks favor over depth alone; a reader still moving never fires.
    idleAfterArmMs: 4000,
    // Timed trigger at the benchmark window for blog posts (Omnisend 45-60s,
    // Popupsmart 30-45s). Fires regardless of depth. Desktop only, see above.
    timeThreshold: coarsePointer ? undefined : 45000,
    // Quiet period before any signal can fire. 6-10s beats immediate in the
    // large popup datasets; under ~5s is the interstitial-penalty zone.
    initialDelay: 6000,
  })

  // On identify-intent blog posts the exit-intent grass-ID scan modal replaces
  // the newsletter body on the same trigger. Every other page keeps the
  // newsletter modal.
  const scanSwap = identifyIntent === true && source === "blog"

  if (scanSwap) {
    return (
      <ExitScanModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        slug={slug}
        category={category}
        triggerSignal={triggerSignal}
      />
    )
  }

  return (
    <NewsletterSignupModal
      isOpen={showNewsletterModal}
      onClose={closeNewsletterModal}
      toolName={source}
      category={category}
      slug={slug}
      triggerSignal={triggerSignal}
      subscriberCount={subscriberCount}
      // Guide pages know the reader's grass — pass it so the care-plan email
      // is species-personalized instead of season-generic.
      grassType={source === "guide" ? category : undefined}
    />
  )
}
